import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { formatVorlageGueltigAb, listGdVorlagen } from "@/lib/gdVorlagen";
import { DOKUMENT_TYP_LABELS } from "@/lib/dokumente";
import type { GdDocumentTemplateListItem } from "@/pages/dokumente/documentTemplateModel";
import { DocumentTemplateEditorDialog } from "@/pages/dokumente/DocumentTemplateEditorDialog";
import { OverviewCard } from "@/projectOverview/OverviewCard";

export function SOrdnerVorlagenSection({ sordnerId }: { sordnerId: number }) {
  const [items, setItems] = useState<GdDocumentTemplateListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listGdVorlagen({
        typ: "angebot",
        sordnerId,
      });
      setItems(Array.isArray(list) ? list : []);
    } catch (err) {
      setItems([]);
      setError(err instanceof Error ? err.message : "Vorlagen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [sordnerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <>
      <OverviewCard title="Angebotsvorlagen">
        <p className="mb-3 text-sm text-label-secondary">
          Vorlagen, die diesem S-Ordner zugewiesen sind. Mehrere Versionen mit Ablösedatum eignen
          sich für jährliche Preis- oder Leistungsanpassungen.
        </p>

        {error ? (
          <Alert variant="error" className="mb-3">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
            <Spinner className="h-4 w-4" />
            Vorlagen werden geladen …
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-label-secondary">
            Diesem S-Ordner sind noch keine Angebotsvorlagen zugewiesen. Weise sie im
            Vorlagen-Editor unter „S-Ordner“ zu.
          </p>
        ) : (
          <ul className="divide-y divide-separator">
            {items.map((item) => {
              const version = item.version ?? 1;
              const next = item.naechste_version;
              return (
                <li key={item.id} className="flex items-start justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-label">{item.name}</div>
                    <div className="mt-0.5 text-xs text-label-secondary">
                      {DOKUMENT_TYP_LABELS[item.dokument_typ]} · Version {version}
                      {item.gueltig_ab
                        ? ` · gültig ab ${formatVorlageGueltigAb(item.gueltig_ab)}`
                        : ""}
                      {item.position_count != null ? ` · ${item.position_count} Positionen` : ""}
                    </div>
                    {next ? (
                      <div className="mt-1 text-xs text-accent">
                        Ab {formatVorlageGueltigAb(next.gueltig_ab)}: Version {next.version}
                      </div>
                    ) : null}
                    {item.beschreibung ? (
                      <div className="mt-0.5 truncate text-xs text-label-tertiary">
                        {item.beschreibung}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="shrink-0"
                    onClick={() => setEditId(item.id)}
                  >
                    Öffnen
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </OverviewCard>

      <DocumentTemplateEditorDialog
        open={editId != null}
        templateId={editId}
        onClose={() => setEditId(null)}
        onSaved={() => void reload()}
      />
    </>
  );
}
